/// <reference path="../../../WebUI/typings/tsd.d.ts" />
/// <reference path="../Views/LogoutPanel.tsx" />
import * as React from "react";
import * as ReactDOM from "react-dom";
import Rest = require("../Generated/RestClient");
import * as Views from "../Views/MasterPage";
import * as Dialogs from "../Dialogs/MessageBox";
import {resourceStrings} from "../../../Intercom.ReactUI/Views/Strings";
import {NavLinks} from "../../../Intercom.ReactUI/Views/JumboHeadControl";
import {DropdownMenu} from "../Views/DropdownMenu";
import {ProgressMaskDialog} from "../Dialogs/ProgressMaskDialog";
import {clientAppSettings} from "../Models/ClientAppSettings";
import {LogoutPanel, LogoutPanelProps} from "../Views/LogoutPanel";
import {DevPortalAppBase} from "../DevPortalAppBase";
import isInFrame from "../../../Intercom.ReactUI/Utils/InFrame";
import {relativeUrl} from "../Utils/Links";
import logger from "../../../Intercom.ReactUI/Utils/logger";
import {parseUserAgent, KnownBrowsers} from "../../../Intercom.ReactUI/Utils/userAgent";

export function isBrowserSupported(): boolean {
    /*
    // check result of parseUserAgent against KnownBrowsers to decide
    // whether we can support the browser or not. Currently we support all browsers
    // ideally we should use browserCaps instead of userAgent to decide
    let agent = parseUserAgent();
    if (agent[KnownBrowser[KnownBrowser.{UnSupportedBrowserBrand}]])
        return false;
    */
    return true;
}

export function refreshClientAppSettings() : JQueryPromise <Rest.ClientAppSettings> {
    return Rest.AccountClient.getClientSettings().done(settings => {
        _.extend(clientAppSettings, settings, _.pick(clientAppSettings, 'homeUrl'));
    });
}

/**
    * This is the base class for all controllers in this app.
    * Here we render parts common to all pages, and handle events in those parts.
    */
export class ControllerBase extends SimpleMvc.Controller {
    protected pageContent: Element;
    protected masterPageState: Views.MasterPageState;
    protected params: SimpleMvc.QueryParams;
    private masterPageComponent: React.Component<Views.MasterPageProps, Views.MasterPageState>;
    private accessToken: string;

    constructor(public app: DevPortalAppBase) {
        super();
    }

    public isAdmin(): boolean {
        return this.app.signedInUser != null && this.app.signedInUser.Roles.indexOf('admin') >= 0;
    }

    public isReviewer(): boolean {
        return this.app.signedInUser != null && this.app.signedInUser.Roles.indexOf('reviewer') >= 0;
    }

    public setSearchState(search: string, searchOpened: boolean) {
        this.masterPageComponent.setState(_.extend(this.masterPageState, { searchString: search, expandSearch: searchOpened }));
    }

    public load(params: SimpleMvc.QueryParams): void {
        super.load(params);
        $(this.app.getAppBody()).off();

        this.params = params;

        if (this.pageRequiresSignIn()) {
            if (!isBrowserSupported()) {
                this.renderCommon(true);
                this.updateState(false, null, () => {
                    // do nothing
                });
            } else if (this.app.signedInUser) {
                this.renderCommon();
                this.updateState(false, null, () => {
                    this.render(params);
                });
            }
            else {
                Rest.AccountClient.getSignedInUser().done(user => {
                    logger.debug('got signed in user');
                    this.app.signedInUser = user;
                    if (!this.setRequestVerificationHeader()) {
                        logger.debug("ControllerBase v3: Failed to set request verification header when getting signed in user.");
                    }
                    this.renderCommon();
                    refreshClientAppSettings().then(
                        () => {
                            this.updateState(false, null, () => this.render(params))
                        }
                    );
                }).fail(() => {
                    if (isInFrame()) {
                        // we are in azure portal as iframe,
                        logger.debug('wait for cross-frame message');
                    } else {
                        this.app.signedInUser = null;
                        this.renderCommon(true);
                    }
                });
            }
        }
        else {
            // check browser support and block if we are hosted by azure portal
            this.renderCommon(isInFrame());
            this.updateState(false, null, () => {
                this.render(params);
            });
        }

        this.addRemoveCssClass(true);

        // Load tracking code here if the user consent to our cookie policy
        this.app.trackingCookieConsent.loadPageTrackingIfInformed();
    }

    public unload(): void {
        this.addRemoveCssClass(false);
        this.app.trackingCookieConsent.markAsInformed();
    }

    private addRemoveCssClass(add: boolean): void {
        var cssClass = this.getCssClass();
        if (cssClass) {
            var $appBody = $(this.app.getAppBody());
            if (add) {
                $appBody.addClass(cssClass);
            }
            else {
                $appBody.removeClass(cssClass);
            }
        }
    }

    /**
        *  Controllers that want to add a custom CSS class to app-body any time they are loaded should override this method.
        */
    protected getCssClass(): string {
        return null;
    }

    protected pageRequiresSignIn(): boolean {
        return true;
    }

    protected refreshSignedInUser() {
        // synchronize csrf cookie and header value
        if (!this.setRequestVerificationHeader()) {
            logger.debug("ControllerBase v3: Failed to set request verification header when refreshing signed in user.");
        }
        if (!this.app.signedInUser) {
            logger.debug('begin refresh signed user');
            this.load(this.params);
        }
    }

    /**
        * Renders parts common to all pages.
        * This method is called whether or not the user is signed in.
        */
    protected renderCommon(checkBrowserSupport?: boolean): void {
        var props: Views.MasterPageProps = {
            isAdmin: () => this.isAdmin(),
            isReviewer: () => this.isReviewer(),
            signInRequired: this.pageRequiresSignIn(),
            onSearch: (s) => this.handleSearch(s),
            onSignOutClicked: () => this.onSignOutClicked(),
            refreshSignedInUser: () => this.refreshSignedInUser(),
            isRoutableUrl: (url: string) => {
                var appPath = this.app.getAppPath();
                return !(/^https?:/i.test(url) || /^\/\//.test(url));
            }
        };
        var element = React.createElement(Views.MasterPage, props);
        this.masterPageComponent = ReactDOM.render<Views.MasterPageProps, Views.MasterPage>(element, this.app.getAppBody(), () => {
            this.pageContent = $(".page-content")[0];
            this.attachHandlers();
        });

        this.masterPageComponent.setState({ unsupportedBrowser: checkBrowserSupport && !isBrowserSupported() } as any);
    }

    protected updateTitle(title: string) {
        $('head title').text(title || resourceStrings.botFrameworkExtended);
    }

    /**
        * This method is called if the user is signed in.
        * Subclasses should override this method.
        */
    protected render(params: SimpleMvc.QueryParams): void {
        this.updateTitle(null);
        $('.action-sheet').toggleClass("hidden", true);
    }

    protected updateState(signInInProgress: boolean, signInError: string, callback?: () => void): void {
        var curActiveLink = this.masterPageState ? this.masterPageState.activeLink : NavLinks.root;

        this.masterPageState = {
            signInState: this.masterPageState ? this.masterPageState.signInState : null,
            signedInUser: this.app.signedInUser,
            signInInProgress: signInInProgress,
            signInError: signInError,
            activeLink: curActiveLink,
            searchString: null,
        }

        this.masterPageComponent.setState(this.masterPageState, () => {
            this.pageContent = $(".page-content")[0];
            if (callback)
                callback();
        });
    }

    protected setActiveJumboActiveLink(activeLink: NavLinks) {
        if (this.masterPageComponent) {
            this.masterPageState.activeLink = activeLink;
            this.masterPageComponent.setState(this.masterPageState);
        }
    }

    protected handleSearch(s: string) {
        if (s.trim()) {
            this.app.navigate(clientAppSettings.searchRoute || '/bots', { s: s });
        }
    }

    private attachHandlers(): void {
        var $appBody = $(this.app.getAppBody());
        $appBody.on('click', '.signed-in-user', () => this.onSignedInUserClicked());
        $appBody.on('click', '.waitlist-request', () => this.onWaitListButtonClicked());
    }

    private extractErrorFromXHR(xhr: JQueryXHR): string {
        if (xhr && xhr.responseText) {
            try {
                let error = $.parseJSON(xhr.responseText);
                if (error) {
                    error = error.Errors && error.Errors.length > 0 ? error.Errors[0] : error;
                    let message = error.Message || (error.Error ? error.Error.Message : null);
                    if (message) {
                        return message;
                    }
                }
            }
            catch (ex) {
            }
        }
        return null;
    }

    protected handleHttpResponseError(xhr: JQueryXHR): JQueryPromise<any> {
        let errorMessage = this.extractErrorFromXHR(xhr);
        let promise: JQueryPromise<any>;

        if (errorMessage) {
            promise = Dialogs.MessageBox.show(errorMessage);
        }
        else {
            switch (xhr.status) {
                case 400:
                    promise = Dialogs.MessageBox.show("The action or the supplied values are invalid.");
                    break;
                case 401: {
                    let deferred = $.Deferred();
                    this.app.signedInUser = null;
                    this.updateState(false, null, () => { deferred.resolve(null); });
                    promise = deferred.promise();
                    break;
                }
                case 403:
                    promise = Dialogs.MessageBox.show("This item cannot be accessed or modified.");
                    break;
                case 404:
                    promise = Dialogs.MessageBox.show("The item cannot be found.");
                    break;
                case 500:
                    promise = Dialogs.MessageBox.show("An internal error occurred at the server.");
                    break;
                default:
                    if (xhr.statusText)
                        promise = Dialogs.MessageBox.show("Error: " + xhr.statusText);
                    else
                        promise = Dialogs.MessageBox.show("An unknown error occurred.");
                    break;
            }
        }
        return promise;
    }

    public redirectIfNeeded(): boolean {
        var prefix = "?appapiret=";
        var index = window.location.href.indexOf(prefix);
        if (index === -1)
            return false;
        var ret = window.location.href.substr(index + prefix.length);
        if (ret) {
            window.location.assign(decodeURIComponent(ret));
            return true;
        }
        return false;
    }

    private onSignedInUserClicked(): void {
    var dropdownMenu = <DropdownMenu>this.masterPageComponent.refs["userMenu"];
        dropdownMenu.displayBelow($('.signed-in-user'));
    }

    /**
        * Removes unwanted characters from the message.
        */
    private cleanupErrorMessage(message: string): string {
        // Note that we don't want to decode the string because most of the time that will result in double decoding.
        return message.replace(/\+/g, ' ');
    }

    /**
        * This method return false for default error message display to happen.
        */
    private handleIntercomLoginError(xhr: JQueryXHR, accessToken: string): boolean {
        var signInState = xhr.responseJSON.Message;
        switch (signInState) {
            case "SignedUp":
            case "NotSignedUp":
                this.accessToken = accessToken;
                this.masterPageState.signInState = signInState;
                this.updateState(false, null);
                return true;
        }
        return false;
    }

    private onWaitListButtonClicked(): void {
        Rest.AccountClient.addUserToWaitlist().done(() => {
            this.masterPageState.signInState = "SignedUp";
            this.updateState(false, null);
        }).fail(xhr => {
            this.handleHttpResponseError(xhr);
        });
    }

    private onSignOutClicked(): void {
        var path = clientAppSettings.applicationPath === '/' ? '' : clientAppSettings.applicationPath;
        window.location.assign(path + '/signout_all.aspx');
    }

    protected setRequestVerificationHeader(): boolean {
        const requestVerificationToken = ControllerBase.readCookie('Csrf-Token');
        if (requestVerificationToken) {
            Rest.ajaxHeaders = _.extend(Rest.ajaxHeaders || {}, { 'X-Csrf-Token': requestVerificationToken });
        }

        return !!requestVerificationToken;
    }

    public static readCookie(name: string): string {
        var nameEquals = name + '=';
        var cookies = document.cookie.split(';');
        for (var i = 0; i < cookies.length; i++) {
            var cookie = cookies[i].replace(/^\s+/, '');
            if (cookie.indexOf(nameEquals) === 0)
                return cookie.substring(nameEquals.length);
        }
        return null;
    }

    protected scrollToTop(): void {
        window.scrollTo(0, 0);
    }

    public hasUnsavedChanges(): boolean {
        return false;
    }
}



// WEBPACK FOOTER //
// ./Client/Controllers/ControllerBase.ts